import { useEffect, useState } from 'react'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { Box, CircularProgress } from '@mui/material'

import { Button, Stack, Text } from '..'
import { mutations, queries } from '../../graphql'

export default function BankAccountSetupGuest({
  user,
  onSuccess,
  setEnableManualOption,
  refetchGetZumRailsUserGuest,
  setIsPaymentUILoading,
  hideText,
  onBack,
}) {
  const apolloClient = useApolloClient()
  const [isProcessing, setIsProcessing] = useState(false)

  const { data: { guestHasBankAccount } = {}, loading: guestHasBankAccountLoading } = useQuery(
    queries.paymentMethods.guestHasBankAccount,
    {
      variables: { email: user?.email },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      skip: !user?.id,
    }
  )

  const { data: { getZumrailsTokenGuest: addPaymentProfileToken } = {}, refetch: refetchZumrailsTokenGuest } = useQuery(
    queries.paymentMethods.getZumrailsTokenGuest,
    {
      variables: { id: user?.id },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      skip: !user?.id,
    }
  )

  const [createZumRailsUserGuest] = useMutation(mutations.paymentMethods.createZumRailsUserGuest, {
    refetchQueries: [
      {
        query: queries.paymentMethods.guestHasBankAccount,
        variables: { email: user?.email },
      },
    ],
    awaitRefetchQueries: true,
  })

  useEffect(() => {
    if (guestHasBankAccount && !guestHasBankAccountLoading && !isProcessing) {
      onSuccess?.()
    }
  }, [guestHasBankAccount, guestHasBankAccountLoading, isProcessing, onSuccess])

  useEffect(() => setIsProcessing(false), [])

  const handleRefetch = async () => {
    try {
      await apolloClient.refetchQueries({
        include: ['guestHasBankAccount'],
        updateCache: (cache) => {
          cache.evict({ fieldName: 'guestHasBankAccount' })
        },
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error refetching queries:', error)
    }
  }

  const loadIframe = () => {
    const token = addPaymentProfileToken
    setIsProcessing(true)

    // eslint-disable-next-line no-undef
    ZumRailsSDK.init({
      token,
      onError: () => {
        setIsProcessing(false)
        setEnableManualOption?.(true)
      },
      onSuccess: async (data) => {
        try {
          await createZumRailsUserGuest({
            variables: {
              data: {
                id: data.userId,
                clientUserId: data.clientUserId,
              },
            },
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error creating ZumRails user:', error)
        } finally {
          setIsProcessing(false)
          await handleRefetch()
        }
      },
      onButtonClose: () => {
        setIsProcessing(false)
        refetchZumrailsTokenGuest()
        refetchGetZumRailsUserGuest?.()
      },
    })
  }

  useEffect(() => {
    setIsPaymentUILoading?.(true)
  })

  const renderContent = () => {
    if (guestHasBankAccountLoading || isProcessing) {
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <CircularProgress color="info" />
          <div style={{ marginLeft: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Text.Body>
              {isProcessing ? 'Setting up your bank account...' : 'Loading bank account information...'}
            </Text.Body>
          </div>
        </div>
      )
    }

    if (guestHasBankAccount === undefined) {
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <CircularProgress color="info" />
          <div style={{ marginLeft: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Text.Body>Verifying bank account status...</Text.Body>
          </div>
        </div>
      )
    }

    return (
      <Stack spacing={2}>
        {!hideText && <Text.Body>To connect your online bank account, please click the button below.</Text.Body>}
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          <Button onClick={() => onBack('')}>Back</Button>
          <Button onClick={loadIframe} disabled={isProcessing}>
            Connect Bank Account
          </Button>
        </Box>
      </Stack>
    )
  }

  return <Stack spacing={4}>{renderContent()}</Stack>
}
